import { useState, Fragment, useEffect } from 'react'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { Form, Formik, useFormikContext } from 'formik'
import SimpleMaskMoney from 'simple-mask-money'
import * as Analytics from 'core/common/analytics'
import { DEFAULT_VARIATION_OPTIONS_BY_TYPE } from 'core/common/constants/multivariation'
import i18n from 'core/common/i18n'
import { parseNumber } from 'core/common/utils/formikHelpers'
import { isNumber, isBoolean } from 'core/common/utils/is'
import { getErrorMessage, validateField } from 'core/common/utils/validation'
import CardStep from 'core/components/CardStep'
import {
  Button,
  Callout,
  Typography,
  Text,
  OutlinedAdornment,
  OutlinedAddon,
  Grid,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
} from 'core/components/Core'
import BulkModeButton from 'core/components/Multivariation/VariationValue/BulkModeButton'
import { withMask } from 'core/components/TextField'
import VariationItemView from 'core/components/Variations/VariationItemView'
import ProductDialogPriceEditInPromotion from 'core/containers/ProductDialogPriceEditInPromotion'
import useConfirmWhenHasPromotion from 'core/containers/ProductDialogPriceEditInPromotion/useConfirmWhenHasPromotion'
import { useFormContext } from 'core/pages/ProductNew-v2/context/ProductNewContext'
import { SimpleProductPriceFormSchema, VariationProductPriceFormSchema } from './schema'
import * as S from './style'

const fieldTranslations = {
  list_price: 'preco-de',
  price: 'preco-por',
}

const allowedFields = ['list_price', 'price']

const CurrencyInput = withMask(value => SimpleMaskMoney.formatToMask(parseNumber(value)))

export const ProductPriceForm = ({
  onNext,
  isEditMode,
  onEditSubmit,
  navigateToStep,
  handleEditStep,
  step,
  formMode,
}) => {
  const { formData, setFormData } = useFormContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [expanded, setExpanded] = useState([])

  const initialValues = {
    prices: [
      {
        price: formData.prices?.[0]?.price,
        list_price: formData.prices?.[0]?.list_price,
        channel: 'magazineluiza',
        currency: 'BRL',
      },
    ],
    variations: formData.variations,
  }

  const hasVariations = !!formData.variations?.length
  const bulkModeDefault = isBoolean(formData.bulkMode?.priceForm)
    ? formData.bulkMode?.priceForm
    : true

  const [isBulkMode, setBulkMode] = useState(bulkModeDefault)

  const toggleBulkMode = () => {
    setBulkMode(prevBulkMode => !prevBulkMode)
  }

  useEffect(() => {
    if (formData.pendingFieldsVariationFlow) {
      toggleBulkMode()
    }
  }, [formData.pendingFieldsVariationFlow])

  useEffect(() => {
    const submitEdit = async () => {
      if (isSubmitting && isEditMode && onEditSubmit) {
        setIsSubmitting(false)
        setIsLoading(true)
        try {
          await onEditSubmit(formData)
          setIsLoading(false)
        } catch (error) {
          console.error('Error during API call', error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    submitEdit()
  }, [isSubmitting, formData, isEditMode, onEditSubmit])

  const onSubmit = values => {
    Analytics.ProductPriceSaved(formMode)
    setFormData(prevData => ({
      ...prevData,
      prices: values.prices,
      variations: values.variations.map(variation => ({
        ...variation,
        prices: variation.prices.map(price => {
          const newPrice = isBulkMode ? values.prices[0] : price

          return {
            ...newPrice,
            channel: 'magazineluiza',
            currency: 'BRL',
          }
        }),
      })),
      bulkMode: {
        ...(prevData.bulkMode || {}),
        priceForm: isBulkMode,
      },
    }))

    if (formData.pendingFieldsVariationFlow) {
      navigateToStep('publication-2')
    }

    setIsSubmitting(true)
    setExpanded([])
    onNext()
  }

  const handleValidation = async ({ validateForm }) => {
    const errors = await validateForm()
    const { prices, variations } = errors

    if (prices) {
      const errorsPrices = prices[0]

      Object.keys(errorsPrices).forEach(field => {
        if (allowedFields.includes(field)) {
          const translatedField = fieldTranslations[field] || field
          Analytics.ProductPriceValidationError(formMode, translatedField)
        }
      })
    }

    if (variations) {
      variations.forEach(variation => {
        const errorsPrices = variation.prices?.[0]

        Object.keys(errorsPrices).forEach(field => {
          if (allowedFields.includes(field)) {
            const translatedField = fieldTranslations[field] || field
            Analytics.ProductPriceValidationError(formMode, translatedField)
          }
        })
      })
    }
  }

  const handleVariationsValidation = async formik => {
    if (hasVariations && !isBulkMode) {
      const errors = await formik.validateForm()
      const { variations } = formik.values

      if (errors.variations && Array.isArray(errors.variations)) {
        const errorPanels = variations
          .map((variation, index) => (errors.variations[index] ? variation.id : null))
          .filter(Boolean)
        setExpanded(prev => Array.from(new Set([...prev, ...errorPanels])))
      }
    }
  }

  const handleAccordionChange = panel => (event, isExpanded) => {
    setExpanded(prev => (isExpanded ? [...prev, panel] : prev.filter(item => item !== panel)))
  }

  const renderVariationHeader = variationItem => {
    const variationAttributes = variationItem.customAttributes.map(attribute => ({
      label: attribute.value,
      showAvatar: attribute.type === 'color',
      color:
        attribute.type === 'color'
          ? DEFAULT_VARIATION_OPTIONS_BY_TYPE.color.find(
              colorOption => colorOption.title === attribute.value
            )?.color
          : null,
    }))

    return (
      <Box className="d-flex align-center mr-3" key={0}>
        <div
          className="d-flex justify-center mr-2"
          style={{
            borderColor: '#CECFD1',
            borderRadius: 4,
            borderWidth: 1,
            borderStyle: 'solid',
            height: 48,
            width: 48,
            overflow: 'hidden',
          }}
        >
          <img
            src={variationItem.images?.[0]?.url}
            alt="Imagem de uma variação do produto"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>

        {variationAttributes.map((attribute, index) => (
          <Fragment key={index}>
            <VariationItemView variation={attribute} />
            {index !== variationAttributes.length - 1 && <span className="mr-1">/</span>}
          </Fragment>
        ))}
      </Box>
    )
  }

  const renderPricesFieldsByVariation = ({ values }) => {
    return values.variations.map((variationItem, index) => {
      return (
        <div>
          <Accordion
            key={variationItem.id}
            style={{ boxShadow: 'none' }}
            expanded={expanded.includes(variationItem.id)}
            onChange={handleAccordionChange(variationItem.id)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              {renderVariationHeader(variationItem)}
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} className="py-3 px-2">
                <S.PriceBox className="d-flex flex-column px-2 py-3">
                  <div className="mb-3">
                    <Text color="textSecondary" fontWeight="bold">
                      {i18n.t('productNew.price.originalPriceLabel')}
                    </Text>

                    <PriceInput
                      field="list_price"
                      label={i18n.t('productNew.price.priceInputLabel')}
                      channelIndex={0}
                      style={{ backgroundColor: '#fff' }}
                      variationIndex={index}
                      handleEditStep={handleEditStep}
                      step={step}
                    />
                  </div>

                  <div>
                    <Text color="textSecondary" fontWeight="bold">
                      {i18n.t('productNew.price.promotionalPriceLabel')}
                    </Text>

                    <PriceInput
                      field="price"
                      label={i18n.t('productNew.price.priceInputLabel')}
                      channelIndex={0}
                      style={{ backgroundColor: '#fff' }}
                      variationIndex={index}
                      handleEditStep={handleEditStep}
                      step={step}
                    />
                  </div>
                </S.PriceBox>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Divider />
        </div>
      )
    })
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={
        hasVariations && !isBulkMode
          ? VariationProductPriceFormSchema
          : SimpleProductPriceFormSchema
      }
      enableReinitialize
    >
      {formikProps => {
        return (
          <Form className="w-100">
            <CardStep
              title={!isEditMode && i18n.t('productNew.price.title')}
              headerAction={
                hasVariations && (
                  <BulkModeButton isBulkMode={isBulkMode} show onClick={toggleBulkMode} />
                )
              }
            >
              <Callout color="primary" className="py-2 px-3 mb-3 align-center">
                <InfoOutlinedIcon fontSize="small" color="primary" className="mr-1" />
                <Typography component="span" variant="body2" color="textPrimary">
                  {i18n.t('productNew.price.notice')}
                </Typography>
              </Callout>

              {!isBulkMode ? (
                renderPricesFieldsByVariation(formikProps)
              ) : (
                <S.PriceBox className="d-flex flex-column px-2 py-3">
                  <div className="mb-3">
                    <Text color="textSecondary" fontWeight="bold">
                      {i18n.t('productNew.price.originalPriceLabel')}
                    </Text>

                    <PriceInput
                      field="list_price"
                      label={i18n.t('productNew.price.priceInputLabel')}
                      channelIndex={0}
                      style={{ backgroundColor: '#fff' }}
                      handleEditStep={handleEditStep}
                      step={step}
                    />
                  </div>

                  <div>
                    <Text color="textSecondary" fontWeight="bold">
                      {i18n.t('productNew.price.promotionalPriceLabel')}
                    </Text>

                    <PriceInput
                      field="price"
                      label={i18n.t('productNew.price.priceInputLabel')}
                      channelIndex={0}
                      style={{ backgroundColor: '#fff' }}
                      handleEditStep={handleEditStep}
                      step={step}
                    />
                  </div>
                </S.PriceBox>
              )}

              <div className="w-100 d-flex justify-end mt-3">
                {!isEditMode && (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => {
                        formikProps.resetForm()
                        handleEditStep(null)
                      }}
                      className="mr-2"
                    >
                      <span>Cancelar</span>
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        formikProps.handleSubmit()
                        handleVariationsValidation(formikProps)
                        handleValidation({ validateForm: formikProps.validateForm })
                      }}
                    >
                      {i18n.t('productNew.actions.continue')}
                    </Button>
                  </>
                )}

                {isEditMode && (formikProps.dirty || isLoading) && (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => formikProps.resetForm()}
                      className="mr-2"
                    >
                      <span>Cancelar</span>
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        formikProps.handleSubmit()
                        handleVariationsValidation(formikProps)
                        handleValidation({ validateForm: formikProps.validateForm })
                      }}
                      loading={isLoading}
                    >
                      <span>Salvar</span>
                    </Button>
                  </>
                )}
              </div>
            </CardStep>
          </Form>
        )
      }}
    </Formik>
  )
}

const PriceInput = ({
  field,
  label,
  channelIndex,
  style,
  variationIndex,
  handleEditStep,
  step,
}) => {
  const { values, touched, errors, handleBlur, setFieldValue, submitCount } = useFormikContext()
  const isVariation = isNumber(variationIndex)
  const variationPrefix = isVariation ? `variations.${variationIndex}.` : ''

  const name = `${variationPrefix}prices.${channelIndex}.${field}`

  const priceValue = isVariation
    ? values.variations?.[variationIndex]?.prices[channelIndex]?.[field]
    : values.prices[channelIndex]?.[field]

  const dialogHandler = useConfirmWhenHasPromotion({
    product: values,
    priceValue,
    confirmChangeValue: newValue => setFieldValue(name, newValue),
  })
  const { handleChangeValue, handleClick } = dialogHandler

  return (
    <OutlinedAdornment
      position="start"
      error={validateField(name, errors, touched, submitCount > 0)}
    >
      <OutlinedAddon position="start">R$</OutlinedAddon>
      <CurrencyInput
        error={validateField(name, errors, touched, submitCount > 0)}
        helperText={getErrorMessage(name, errors, touched)}
        onBlur={handleBlur}
        fullWidth
        name={name}
        value={priceValue}
        onChange={e => {
          handleChangeValue(e)
          handleEditStep(step.key)
        }}
        onClick={handleClick}
        label={label}
        variant="outlined"
        style={style}
      />
      <ProductDialogPriceEditInPromotion handler={dialogHandler} />
    </OutlinedAdornment>
  )
}
